export default {
  "slug": "notification-appliance-strobe-spacing",
  "title": "Notification Appliance & Strobe Spacing (NFPA 72 Chapter 18)",
  "description": "How visible (strobe) and audible notification appliance spacing works under NFPA 72 Chapter 18 — candela selection, room and corridor spacing tables, synchronization, and the audibility targets that drive coverage.",
  "keywords": "fire alarm strobe spacing calculation, candela selection, notification appliance NFPA 72 chapter 18, audible notification, visible signaling, strobe synchronization",
  "faqs": [
    {
      "q": "How is strobe (candela) rating chosen for a room?",
      "a": "NFPA 72 Chapter 18 provides room-spacing tables that relate room size and mounting to a minimum candela rating for wall- or ceiling-mounted strobes. Larger rooms or a single centrally located strobe require higher candela; multiple strobes can cover a large space when spaced and rated per the tables."
    },
    {
      "q": "What audibility level does the code target?",
      "a": "For public-mode audible signaling, NFPA 72 generally requires the alarm to be a set margin above ambient sound — commonly 15 dB above average ambient or 5 dB above the maximum sound level lasting 60 seconds, measured at the design location. Sleeping areas and private-mode signaling have their own criteria."
    },
    {
      "q": "Why do strobes need to be synchronized?",
      "a": "Multiple unsynchronized strobes visible at once can produce flash rates that risk photosensitive reactions. Chapter 18 requires strobes within a field of view to be synchronized so the combined flash rate stays within safe limits."
    }
  ],
  "category": "Design",
  "readMins": 6,
  "published": "2026-09-27",
  "updated": "2026-09-27",
  "hero": "https://images.unsplash.com/photo-1653353436174-a8e724190fd3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxmaXJlJTIwYWxhcm0lMjBjb250cm9sJTIwcGFuZWwlMjB3aXJpbmd8ZW58MHx8fHwxNzkwNTI2MDE0fDA&ixlib=rb-4.1.0&q=85",
  "heroAlt": "Red fire alarm notification device mounted on a wall",
  "excerpt": "Notification is about reaching everyone — by sound and by sight. Here's how Chapter 18 turns that goal into candela ratings and spacing you can lay out.",
  "sections": [
    {
      "h": "Two jobs: be heard and be seen",
      "p": "Notification appliances have to alert occupants who can hear (audible) and occupants who can't (visible). NFPA 72 Chapter 18 sets performance targets for both, then gives spacing tables and rules so a designer can translate those targets into device count, candela ratings, and mounting locations."
    },
    {
      "h": "Visible signaling — candela and room spacing",
      "p": "Strobe effectiveness depends on light output (candela) and geometry. Chapter 18's room-spacing tables map room dimensions and strobe placement to a minimum candela. A single strobe centered in a room needs a higher rating than the same room covered by strobes placed per the four-strobe or perimeter arrangements the tables allow. Corridors use their own linear spacing rules based on candela."
    },
    {
      "h": "Audible signaling — margin above ambient",
      "p": "Audible coverage is driven by the ambient sound of the space. Public-mode signaling typically must exceed average ambient by 15 dB, or exceed a 60-second maximum by 5 dB, at the design point. Because sound attenuates through walls and distance, loud mechanical spaces need more or louder appliances than quiet offices."
    },
    {
      "h": "Synchronization and combined effects",
      "p": "Where more than one strobe is visible from a location, Chapter 18 requires synchronization to control the aggregate flash rate. Synchronization modules or self-synchronizing appliances handle this. The same discipline helps audible clarity when combined with voice systems, where tones and messages must not mask each other."
    },
    {
      "h": "From targets to a layout",
      "p": "A practical workflow: establish ambient sound levels and occupancy for each space, select candela from the room-spacing tables, place appliances to satisfy both audible margin and visible coverage, then verify synchronization and circuit capacity (voltage drop and current) so every appliance still meets its listed operating range. Always confirm against the adopted edition and any local amendments."
    }
  ],
  "relatedModules": [
    "design-principles",
    "emergency-comm"
  ],
  "relatedGuides": [
    "voice-evacuation-intelligibility",
    "fire-alarm-system-design-basics",
    "voltage-drop-calculation"
  ]
};
